import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { listClientes, listEtiquetas, upsertCliente, type ClienteListItem } from "@/lib/clientes.functions";
import { ClienteFichaSheet } from "@/components/gestion/clientes/ClienteFichaSheet";
import { Plus, Search, Users, Repeat, Star, TrendingUp, Download } from "lucide-react";
import { format, parseISO, differenceInMonths } from "date-fns";
import { es } from "date-fns/locale";

export const Route = createFileRoute("/_authenticated/gestion/clientes")({
  component: ClientesPage,
});

type Orden = "ultima" | "gastado" | "reservas" | "nombre";
type Segmento = "todos" | "repetidores" | "vip" | "nuevos" | "inactivos";

function ClientesPage() {
  const list = useServerFn(listClientes);
  const listTags = useServerFn(listEtiquetas);
  const upsert = useServerFn(upsertCliente);

  const [items, setItems] = useState<ClienteListItem[] | null>(null);
  const [tags, setTags] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [tagFilter, setTagFilter] = useState<string>("all");
  const [segmento, setSegmento] = useState<Segmento>("todos");
  const [orden, setOrden] = useState<Orden>("ultima");
  const [selected, setSelected] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ nombre: "", email: "", telefono: "" });

  async function reload() {
    try {
      const [data, t] = await Promise.all([list(), listTags()]);
      setItems(data);
      setTags(t);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  const kpis = useMemo(() => {
    if (!items) return { total: 0, repetidores: 0, vip: 0, nuevos: 0, ingresoMedio: 0 };
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const repetidores = items.filter((c) => c.num_reservas >= 2).length;
    const vip = items.filter((c) => c.etiquetas.some((e) => e.nombre.toLowerCase() === "vip")).length;
    const nuevos = items.filter((c) => c.created_at >= firstOfMonth).length;
    const conGasto = items.filter((c) => c.total_gastado > 0);
    const ingresoMedio = conGasto.length ? conGasto.reduce((a, c) => a + c.total_gastado, 0) / conGasto.length : 0;
    return { total: items.length, repetidores, vip, nuevos, ingresoMedio };
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const today = new Date();
    const ql = q.toLowerCase().trim();
    const out = items.filter((c) => {
      if (ql && !`${c.nombre} ${c.email ?? ""} ${c.telefono ?? ""}`.toLowerCase().includes(ql)) return false;
      if (tagFilter !== "all" && !c.etiquetas.some((e) => e.id === tagFilter)) return false;
      if (segmento === "repetidores" && c.num_reservas < 2) return false;
      if (segmento === "vip" && !c.etiquetas.some((e) => e.nombre.toLowerCase() === "vip")) return false;
      if (segmento === "nuevos") {
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();
        if (c.created_at < firstOfMonth) return false;
      }
      if (segmento === "inactivos") {
        if (!c.ultima_estancia) return false;
        if (differenceInMonths(today, parseISO(c.ultima_estancia)) < 12) return false;
      }
      return true;
    });
    out.sort((a, b) => {
      switch (orden) {
        case "gastado": return b.total_gastado - a.total_gastado;
        case "reservas": return b.num_reservas - a.num_reservas;
        case "nombre": return (a.nombre || a.email || "").localeCompare(b.nombre || b.email || "");
        case "ultima":
        default: return (b.ultima_estancia ?? "").localeCompare(a.ultima_estancia ?? "");
      }
    });
    return out;
  }, [items, q, tagFilter, segmento, orden]);

  function exportCSV() {
    const headers = ["nombre", "email", "telefono", "reservas", "gastado", "ultima_estancia", "proxima_estancia", "etiquetas"];
    const rows = filtered.map((c) => [
      c.nombre, c.email ?? "", c.telefono ?? "", c.num_reservas, c.total_gastado.toFixed(2),
      c.ultima_estancia ?? "", c.proxima_estancia ?? "", c.etiquetas.map((e) => e.nombre).join("|"),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clientes_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleCreate() {
    if (!newForm.nombre && !newForm.email) return toast.error("Indica al menos nombre o email");
    try {
      const { id } = await upsert({ data: newForm as any });
      toast.success("Cliente creado");
      setNewOpen(false);
      setNewForm({ nombre: "", email: "", telefono: "" });
      await reload();
      setSelected(id);
      setOpen(true);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiCard icon={Users} label="Clientes" value={kpis.total} />
        <KpiCard icon={Repeat} label="Repetidores" value={kpis.repetidores} />
        <KpiCard icon={Star} label="VIP" value={kpis.vip} />
        <KpiCard icon={TrendingUp} label="Nuevos este mes" value={kpis.nuevos} />
        <KpiCard icon={TrendingUp} label="Ingreso medio" value={`${kpis.ingresoMedio.toFixed(0)} €`} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar nombre, email o teléfono…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={tagFilter} onValueChange={setTagFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Etiqueta" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las etiquetas</SelectItem>
            {tags.map((t: any) => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={segmento} onValueChange={(v) => setSegmento(v as Segmento)}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="repetidores">Repetidores</SelectItem>
            <SelectItem value="vip">VIP</SelectItem>
            <SelectItem value="nuevos">Nuevos este mes</SelectItem>
            <SelectItem value="inactivos">Inactivos +12m</SelectItem>
          </SelectContent>
        </Select>
        <Select value={orden} onValueChange={(v) => setOrden(v as Orden)}>
          <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ultima">Última estancia</SelectItem>
            <SelectItem value="gastado">Más gastado</SelectItem>
            <SelectItem value="reservas">Más reservas</SelectItem>
            <SelectItem value="nombre">Alfabético</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV}><Download className="mr-2 h-4 w-4" /> CSV</Button>
          <Dialog open={newOpen} onOpenChange={setNewOpen}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="mr-2 h-4 w-4" /> Nuevo cliente</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuevo cliente</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nombre</Label><Input value={newForm.nombre} onChange={(e) => setNewForm({ ...newForm, nombre: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} /></div>
                <div><Label>Teléfono</Label><Input value={newForm.telefono} onChange={(e) => setNewForm({ ...newForm, telefono: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setNewOpen(false)}>Cancelar</Button>
                <Button onClick={handleCreate}>Crear</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Cliente</th>
                  <th className="px-3 py-2">Contacto</th>
                  <th className="px-3 py-2">Etiquetas</th>
                  <th className="px-3 py-2 text-center">Reservas</th>
                  <th className="px-3 py-2 text-right">Gastado</th>
                  <th className="px-3 py-2">Última</th>
                  <th className="px-3 py-2">Próxima</th>
                </tr>
              </thead>
              <tbody>
                {items === null && Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-t"><td colSpan={7} className="p-3"><Skeleton className="h-8 w-full" /></td></tr>
                ))}
                {items && filtered.length === 0 && (
                  <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No se han encontrado clientes con los filtros actuales.</td></tr>
                )}
                {filtered.map((c) => {
                  const initials = (c.nombre || c.email || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
                  return (
                    <tr key={c.id} className="cursor-pointer border-t hover:bg-secondary/40" onClick={() => { setSelected(c.id); setOpen(true); }}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-xs text-primary">{initials}</AvatarFallback></Avatar>
                          <span className="font-medium">{c.nombre || <span className="text-muted-foreground italic">sin nombre</span>}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs">{c.email}</div>
                        <div className="text-xs text-muted-foreground">{c.telefono}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {c.etiquetas.slice(0, 3).map((e) => (
                            <Badge key={e.id} variant="outline" className="text-[10px]" style={{ backgroundColor: e.color + "22", color: e.color, borderColor: e.color + "55" }}>{e.nombre}</Badge>
                          ))}
                          {c.etiquetas.length > 3 && <span className="text-xs text-muted-foreground">+{c.etiquetas.length - 3}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center font-medium">{c.num_reservas}</td>
                      <td className="px-3 py-2 text-right font-semibold">{c.total_gastado.toFixed(2)} €</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{c.ultima_estancia ? format(parseISO(c.ultima_estancia), "d MMM yy", { locale: es }) : "—"}</td>
                      <td className="px-3 py-2 text-xs">{c.proxima_estancia ? <span className="font-medium text-primary">{format(parseISO(c.proxima_estancia), "d MMM yy", { locale: es })}</span> : <span className="text-muted-foreground">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <ClienteFichaSheet clienteId={selected} open={open} onOpenChange={setOpen} onChanged={reload} />
    </div>
  );
}

function KpiCard({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="rounded-md bg-primary/10 p-2 text-primary"><Icon className="h-5 w-5" /></div>
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-lg font-semibold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}